import { Injectable } from '@angular/core';

export type LogoutReason = 'manual' | 'token_expired' | 'inactive';

const LOGOUT_REASON_KEY = 'logout_reason';
export const LOGOUT_RETURN_URL_KEY = 'logout_return_url';
export const LOGOUT_IN_PROGRESS_KEY = '__logout_in_progress__';

@Injectable({
  providedIn: 'root'
})
export class LogoutService {
  constructor() {}

  /** Store the latest logout reason for the next login screen to consume */
  setLogoutReason(reason: LogoutReason): void {
    sessionStorage.setItem(LOGOUT_REASON_KEY, reason);
  }

  /** Read the last logout reason without clearing it */
  getLogoutReason(): LogoutReason | null {
    return (sessionStorage.getItem(LOGOUT_REASON_KEY) as LogoutReason | null) ?? null;
  }

  /** Read and clear the last logout reason */
  consumeLogoutReason(): LogoutReason | null {
    const reason = this.getLogoutReason();
    sessionStorage.removeItem(LOGOUT_REASON_KEY);
    return reason;
  }

  /** Clear any stored logout reason */
  clearLogoutReason(): void {
    sessionStorage.removeItem(LOGOUT_REASON_KEY);
  }

  /** Store the return URL only once, skipping the login route itself */
  setReturnUrl(returnUrl: string): void {
    if (!returnUrl || returnUrl.includes('/login')) {
      return;
    }

    sessionStorage.setItem(LOGOUT_RETURN_URL_KEY, returnUrl);
  }

  /** Read the saved return URL without clearing it */
  getReturnUrl(): string | null {
    return sessionStorage.getItem(LOGOUT_RETURN_URL_KEY);
  }

  /** Remove any saved return URL */
  clearReturnUrl(): void {
    sessionStorage.removeItem(LOGOUT_RETURN_URL_KEY);
  }

  /** Read and clear the saved return URL */
  consumeReturnUrl(): string | null {
    const returnUrl = sessionStorage.getItem(LOGOUT_RETURN_URL_KEY);
    if (returnUrl) {
      sessionStorage.removeItem(LOGOUT_RETURN_URL_KEY);
    }

    return returnUrl;
  }

  /** Clear the re-entrancy guard once logout is finished */
  clearLogoutInProgress(): void {
    sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
  }

  /** Redirect the user to the login page and remember why the session ended */
  logout(reason: LogoutReason = 'manual') {
    if (sessionStorage.getItem(LOGOUT_IN_PROGRESS_KEY)) {
      return;
    }

    sessionStorage.setItem(LOGOUT_IN_PROGRESS_KEY, 'true');

    this.setLogoutReason(reason);
    localStorage.clear();
    window.location.href = '/login';
  }
}
